import * as Actions from '../actions/resourceActions';
import Resource from '../../entities/resource';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type ResourceAction = { 
    type: string; 
    resource: Resource | null; 
    resourceId: string | Resource; 
    partial: boolean; 
    lastFetch: number; 
    resources: Resource[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Resource> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Resource[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Resource> | null, firmId: string, userId: string, updates?: Resource[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'resource', 'resources', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Resource>} data
 * @param {Resource[]} [updates]
 * @returns {ArrayMap<Resource>}
 */
function updateStorage(data: ArrayMap<Resource>, updates?: Resource[]): ArrayMap<Resource> {
	if (logging) console.debug('⮂ resourceReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Resource[]} [updates]
 * @returns {ArrayMap<Resource>}
 */
function updateStorageNoReturn(updates?: Resource[]): void {
	if (logging) console.debug('⮂ resourceReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Resource[]} [data]
 * @returns {ArrayMap<Resource>}
 */
function createState(data?: Resource[] | ArrayMap<Resource>): ArrayMap<Resource> {
	if (!data) return ArrayMapRegistry.clone<Resource>('resource', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Resource>('resource', data.map(e => e));
    else return ArrayMapRegistry.clone<Resource>('resource', data);
}

function resources(state: ArrayMap<Resource>, action: ResourceAction): ArrayMap<Resource> {
	if (!state) state = createState();

	let st: ArrayMap<Resource>;

	switch (action.type) {
		// action.resource - the new value of resource with id action.id
		// action.resourceId - id of the target, used if new value is null
		case Actions.RESOURCE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ resourceReducer: FETCH_ONE_SUCCESS', action);

			if (action.resource === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.resourceId)) {
				st = state.clone();
				st.set(action.resource);
				// state.clear();

				updateStorageNoReturn([action.resource]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.resources
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.resources
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.resources
			 !partial, !stateEmpty, fetchEmpty	-> D return resources in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge resources in state newer than those in action.resources
		 */
		case Actions.RESOURCE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.resources.length === 0;
			const fetched = action.resources.map(resource => new Resource({...resource, persisted: true}));

			if (logging) console.debug(`⮂ resourceReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} resources with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ resourceReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ resourceReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Resource> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Resource[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(resource => {
					const current = st.get(resource.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && resource.status === 'D') {
							// Remove it from state
							toUpdate.push(resource);
							st.delete(resource);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (resource.addedOn > current.addedOn) {
								toUpdate.push(resource);
								st.set(resource);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (resource.status !== 'D') {
							toUpdate.push(resource);
							st.set(resource);
						}
						else if (logging) console.debug('⮂ resourceReducer: partialMerge: skipping deleted resource not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ resourceReducer: empty: resources', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ resourceReducer: fullMerge: resources', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localResource => {
                    // If this resource has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localResource.persisted) {
                        const fetchedResource = st.get(localResource.id);
                        if (!fetchedResource || localResource.addedOn > fetchedResource.addedOn) st.set(localResource);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.RESOURCE_FETCH_ONE_FAILURE:
			console.error('RESOURCE_FETCH_ONE_FAILURE', action.resourceId, action.err);

			// No update to storage required
			return state;

		case Actions.RESOURCE_FETCH_FAILURE:
			console.error('RESOURCE_FETCH_FAILURE', action.resourceId, action.err);

			// No update to storage required
			return state;

		case Actions.RESOURCE_RESTORE: {
		    let resource = new Resource({...action.resource});
            st = state.clone();

            const existing = st.get(resource.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (resource.addedOn >= existing.addedOn) {
                    st.set(resource);
                    return updateStorage(st, [resource]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (resource.status === 'D') return state;

                st.set(resource);
                return updateStorage(st, [resource]);
            }
        }
            
		case Actions.RESOURCE_SAVE: {
		    // console.debug('⮂ RESOURCE_SAVE reducer', action.resource?.id);
		    
			// clone the resource
			let resource = new Resource({...action.resource});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new resource in the state
			// If this is a create, add to the queue
			const _resource = st.get(resource.id);

			if (!_resource) st.set(resource);
			else {
				if (resource.addedOn >= _resource.addedOn) st.set(resource);
			}

			return updateStorage(st, [resource]);
		}

		case Actions.RESOURCE_SAVE_MANY: {
		    if (logging) console.debug('⮂ RESOURCE_SAVE_MANY reducer', action.resources.map(resource => resource.id).join(','));
		    
			// clone the resources
			let toProcess = action.resources.map(resource => new Resource(resource));

			st = state.clone();
			// state.clear();

			const updates: Resource[] = [];

			toProcess.forEach(resource => {
				// Add or update the new resource in the state
				// If this is a create, add to the queue
				const existing = st.get(resource.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (resource.status !== 'D') {
						st.push(resource);
						updates.push(resource);
					}
					else if (logging) console.debug('⮂ RESOURCE_SAVE_MANY: skipping deleted resource not in state');
				}
				else {
					if (resource.addedOn >= existing.addedOn) {
						st.set(resource);
						updates.push(resource);
					}
					else if (logging) console.debug('⮂ Skipping, existing resource had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.RESOURCE_REMOVE: {
			// clone the resource
			const resource = new Resource({...action.resource});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(resource);

			return updateStorage(st, [resource]);
		}

		case Actions.RESOURCE_RESTORE_FAILURE:
		case Actions.RESOURCE_SAVE_FAILURE: {
		    console.warn('RESOURCE_SAVE_FAILURE reducer', action.resource?.id);
		    if (!action.resource?.id) {
				console.warn('RESOURCE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const resource = ensure(Resource, action.resource);
			if (!resource) {
				console.error('Could not find resource after call to save');
				return state;
			}

			st = state.clone();

			const _resourceRef = state.get(resource.id);

			if (_resourceRef && (_resourceRef.addedOn === resource.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _resource = new Resource({..._resourceRef});
				_resource.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_resource.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('resource', _resource).then(r => {
						if (r.isErr()) console.error(`⮂ resourceReducer: failed to save resource with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ resourceReducer: saved resource with fatalError info');
					}).catch(err => console.error(`⮂ resourceReducer: failed to save resource with fatalError info: ${err.message}`));

				st.set(_resource);
			}

			return updateStorage(st);
		}

		case Actions.RESOURCE_SAVE_MANY_FAILURE: {
		    console.warn('RESOURCE_SAVE_MANY_FAILURE reducer', action.resources.map(resource => resource.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.RESOURCE_RESTORE_SUCCESS:
		case Actions.RESOURCE_SAVE_SUCCESS: {
			if (!action.resource?.id) {
				console.warn('RESOURCE_SAVE_SUCCESS reducer');
                return state;
			}

			const resourceRef = state.get(action.resource?.id);
			if (!resourceRef) {
				console.warn('Could not find resource after call to save');
                return state;
			}

			st = state.clone();

			if (action.resource.addedOn >= resourceRef.addedOn) {
				st.set(action.resource);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const resource = new Resource({...resourceRef, persisted: true});
				st.set(resource);
			}

			return updateStorage(st, [action.resource]);
		}

		case Actions.RESOURCE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ RESOURCE_SAVE_MANY_SUCCESS reducer', action.resources.map(resource => resource.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const resources: Resource[] = [];

			action.resources.forEach(e => {
				const resourceRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!resourceRef) console.warn('Could not find resource after call to save');
				else if (e.addedOn >= resourceRef.addedOn) {
				    st.set(e);
				    resources.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const resource = new Resource({...resourceRef, persisted: true});
					st.set(resource);
					resources.push(resource);
				}
			});

			return updateStorage(st, resources);
		}

		case Actions.RESOURCE_REMOVE_FAILURE: {
		    console.warn('RESOURCE_REMOVE_FAILURE action', action.resource?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.RESOURCE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.RESOURCE_RESTORE_MANY: {
			if (logging) console.debug('⮂ RESOURCE_RESTORE_MANY reducer', action.resources.map(resource => resource.id).join(','));

			const toProcess = action.resources.map(resource => new Resource({...resource}));
			st = state.clone();

			const updates: Resource[] = [];

			toProcess.forEach(resource => {
				const existing = st.get(resource.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (resource.addedOn >= existing.addedOn) {
						st.set(resource);
						updates.push(resource);
					}
					else if (logging) console.debug('⮂ RESOURCE_RESTORE_MANY: skipping older version of resource');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (resource.status !== 'D') {
						st.set(resource);
						updates.push(resource);
					}
					else if (logging) console.debug('⮂ RESOURCE_RESTORE_MANY: skipping deleted resource not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.RESOURCE_REMOVE_MANY: {
			if (logging) console.debug('⮂ RESOURCE_REMOVE_MANY reducer', action.resources.map(resource => resource.id).join(','));

			const toProcess = action.resources.map(resource => new Resource({...resource}));
			st = state.clone();

			toProcess.forEach(resource => {
				st.delete(resource);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default resources;